import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import Drawer from '../common/Drawer';
import FirearmSpecificExerciseForm from './FirearmSpecificExerciseForm';
import { Pencil, Trash2 } from 'lucide-react';
import {
  FirearmSpecificDTO,
  FirearmTypeDTO,
  FirearmCalibreDTO,
  FirearmActionDTO,
  FirearmBarrelDTO,
  FirearmPurposeDTO,
  FirearmSpecificExerciseDTO,
  ExerciseTypeDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFirearmTypes } from '../../hooks/useFirearmTypeApi';
import { useFirearmCalibres } from '../../hooks/useFirearmCalibreApi';
import { useFirearmActions } from '../../hooks/useFirearmActionApi';
import { useFirearmBarrels } from '../../hooks/useFirearmBarrelApi';
import { useFirearmPurposes } from '../../hooks/useFirearmPurposeApi';
import { useExerciseTypes } from '../../hooks/useExerciseTypeApi';
import { renderBooleanBadge } from '../../utils/displayUtils';
import Badge from '../common/Badge';
import { ValidationRules, useFormValidation } from '../../hooks/useFormValidation';
import AddButton from '../common/AddButton';

interface FirearmSpecificFormProps {
  initialValues: FirearmSpecificDTO;
  onSubmit: (data: FirearmSpecificDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  types: {
    id: 'types',
    title: 'Type Settings',
  },
  specifications: {
    id: 'specifications',
    title: 'Specifications',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
  exercises: {
    id: 'exercises',
    title: 'Exercises',
  },
} as const;

const FirearmSpecificForm: React.FC<FirearmSpecificFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FirearmSpecificDTO>(initialValues);

   const validationRules: ValidationRules = {
      title: [
        { required: true, message: 'Title is required' },
      ],
      displayPosition: [
        { required: true, message: 'Position is required' },
        { 
          custom: (value) => Number(value) >= 0, 
          message: 'Position must be a positive number' 
        }
      ]
    };
  
    const { errors, validate } = useFormValidation<FirearmSpecificDTO>(validationRules);

  // Options states for multiselects
  const [calibreOptions, setCalibreOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [actionOptions, setActionOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [barrelOptions, setBarrelOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [purposeOptions, setPurposeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // Maps for keeping full DTOs
  const [calibreMap, setCalibreMap] = useState<Map<string, FirearmCalibreDTO>>(
    new Map(),
  );
  const [actionMap, setActionMap] = useState<Map<string, FirearmActionDTO>>(
    new Map(),
  );
  const [barrelMap, setBarrelMap] = useState<Map<string, FirearmBarrelDTO>>(
    new Map(),
  );
  const [purposeMap, setPurposeMap] = useState<Map<string, FirearmPurposeDTO>>(
    new Map(),
  );

  // Cached TQ hooks for dropdown data
  const { data: firearmTypes = [] } = useFirearmTypes();
  const { data: calibres = [] } = useFirearmCalibres();
  const { data: actions = [] } = useFirearmActions();
  const { data: barrels = [] } = useFirearmBarrels();
  const { data: purposes = [] } = useFirearmPurposes();
  const { data: exerciseTypes = [] } = useExerciseTypes();

  const [isExerciseDrawerOpen, setIsExerciseDrawerOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<{
    exerciseIndex: number;
    specific: FirearmSpecificExerciseDTO;
  } | null>(null);

  const handleAddExercise = (exercise: FirearmSpecificExerciseDTO) => {
    setFormData((prev) => ({
      ...prev,
      exercises: [...(prev.exercises || []), exercise],
    }));
    setIsExerciseDrawerOpen(false);
  };

  const handleExerciseSubmit = (exercise: FirearmSpecificExerciseDTO) => {
    if (selectedExercise) {
      // Update existing exercise
      setFormData((prev) => ({
        ...prev,
        exercises: prev.exercises?.map((e, i) =>
          i === selectedExercise.exerciseIndex ? exercise : e,
        ),
      }));
    } else {
      // Add new exercise
      setFormData((prev) => ({
        ...prev,
        exercises: [...(prev.exercises || []), exercise],
      }));
    }
    setIsExerciseDrawerOpen(false);
    setSelectedExercise(null);
  };

  // Add this helper function inside your component
  const getUsedExerciseTypeIds = () => {
    return new Set(formData.exercises?.map(e => e.exerciseTypeId) || []);
  };

  // Reactive effects for TQ cached data
  useEffect(() => {
    if (calibres.length > 0) {
      setCalibreOptions(
        calibres.map((c) => ({
          value: c.id as string,
          label: c.title || '',
        })),
      );
      setCalibreMap(new Map(calibres.map((c) => [c.id as string, c])));
    }
  }, [calibres]);

  useEffect(() => {
    if (actions.length > 0) {
      setActionOptions(
        actions.map((a) => ({ value: a.id as string, label: a.title || '' })),
      );
      setActionMap(new Map(actions.map((a) => [a.id as string, a])));
    }
  }, [actions]);

  useEffect(() => {
    if (barrels.length > 0) {
      setBarrelOptions(
        barrels.map((b) => ({ value: b.id as string, label: b.title || '' })),
      );
      setBarrelMap(new Map(barrels.map((b) => [b.id as string, b])));
    }
  }, [barrels]);

  useEffect(() => {
    if (purposes.length > 0) {
      setPurposeOptions(
        purposes.map((p) => ({
          value: p.id as string,
          label: p.title || '',
        })),
      );
      setPurposeMap(new Map(purposes.map((p) => [p.id as string, p])));
    }
  }, [purposes]);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
        <FormField
          id="firearmTypeId"
          label="Firearms Type"
          type="dropdown"
          value={formData.firearmTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Firearms Type"
          options={[
            ...firearmTypes.map((type) => ({
              value: type.id,
              label: type.title || '',
            })),
          ]}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.types.id}
        title={FORM_SECTIONS.types.title}
      >
        <FormField
          id="notOnList"
          label="Not On List?"
          type="checkbox"
          value={formData.notOnList || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to show not on list option."
        />
        <FormField
          id="barrelLength"
          label="Barrel Length?"
          type="checkbox"
          value={formData.barrelLength || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to show barrel length field."
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.specifications.id}
        title={FORM_SECTIONS.specifications.title}
      >
        <FormField
          id="calibres"
          label="Calibres"
          type="multiselect"
          value={(formData.calibres || []).map((c) => c.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            const selectedCalibres = selectedIds
              .map((id) => calibreMap.get(id))
              .filter((c): c is FirearmCalibreDTO => c !== undefined);
            handleChange(
              { target: { id: 'calibres', value: selectedCalibres } } as any,
              setFormData,
            );
          }}
          options={calibreOptions}
          placeholder="Select calibres..."
          helper="Select one or more calibres"
        />
        <FormField
          id="actions"
          label="Actions"
          type="multiselect"
          value={(formData.actions || []).map((a) => a.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            const selectedActions = selectedIds
              .map((id) => actionMap.get(id))
              .filter((a): a is FirearmActionDTO => a !== undefined);
            handleChange(
              { target: { id: 'actions', value: selectedActions } } as any,
              setFormData,
            );
          }}
          options={actionOptions}
          placeholder="Select actions..."
          helper="Select one or more actions"
        />
        <FormField
          id="barrels"
          label="Barrels"
          type="multiselect"
          value={(formData.barrels || []).map((b) => b.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            const selectedBarrels = selectedIds
              .map((id) => barrelMap.get(id))
              .filter((b): b is FirearmBarrelDTO => b !== undefined);
            handleChange(
              { target: { id: 'barrels', value: selectedBarrels } } as any,
              setFormData,
            );
          }}
          options={barrelOptions}
          placeholder="Select barrels..."
          helper="Select one or more barrels"
        />
        <FormField
          id="purposes"
          label="Purposes"
          type="multiselect"
          value={(formData.purposes || []).map((p) => p.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            const selectedPurposes = selectedIds
              .map((id) => purposeMap.get(id))
              .filter((p): p is FirearmPurposeDTO => p !== undefined);
            handleChange(
              { target: { id: 'purposes', value: selectedPurposes } } as any,
              setFormData,
            );
          }}
          options={purposeOptions}
          placeholder="Select purposes..."
          helper="Select one or more purposes"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Display Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter DisplayPosition"
          helper="Lower numbers will be displayed first."
          required
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.exercises.id}
        title={FORM_SECTIONS.exercises.title}
        headerActions={
          exerciseTypes.length > (formData.exercises?.length || 0) ? (
            <AddButton
              onClick={() => setIsExerciseDrawerOpen(true)}
              variant="secondary"
              label="Add Exercise"
            />
          ) : (
            <AddButton
              onClick={() => {}}
              variant="secondary"
              label="All exercise types have been added"
              disabled
            />
          )
        }
      >
        {formData.exercises && formData.exercises.length > 0 ? (
          <div className="space-y-4">
            {formData.exercises.map((exercise, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-lg">
                    {
                      exerciseTypes.find(
                        (t) => t.id === exercise.exerciseTypeId,
                      )?.title
                    }
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                      onClick={() => {
                        setIsExerciseDrawerOpen(true);
                        setSelectedExercise({
                          exerciseIndex: index,
                          specific: exercise,
                        });
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          exercises: prev.exercises?.filter(
                            (_, i) => i !== index,
                          ),
                        }));
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {exercise.exerciseSpecifics &&
                exercise.exerciseSpecifics.length > 0 ? (
                  <div className="ml-4 space-y-2">
                    {exercise.exerciseSpecifics.map((specific) => (
                      <div
                        key={specific.id}
                        className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                      >
                        <div className="flex flex-col space-y-2">
                          <span className="font-medium">{specific.title}</span>
                          <div className="flex flex-wrap gap-2">
                            {specific.isComplianceHunt && (
                              <Badge
                                label="Hunt Compliance"
                                variant="success"
                              />
                            )}
                            {specific.isComplianceSport && (
                              <Badge
                                label="Sport Compliance"
                                variant="success"
                              />
                            )}
                            {specific.isEvaluationHunt && (
                              <Badge
                                label="Hunt Evaluation"
                                variant="success"
                              />
                            )}
                            {specific.isEvaluationSport && (
                              <Badge
                                label="Sport Evaluation"
                                variant="success"
                              />
                            )}
                            {specific.isCompetition && (
                              <Badge label="Competition" variant="success" />
                            )}
                            {specific.isHuntActivity && (
                              <Badge label="Hunt Activity" variant="success" />
                            )}
                            {specific.isSportShootingActivity && (
                              <Badge label="Sport Shooting" variant="success" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm ml-4">
                    No exercise specifics added.
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No exercises added yet.</p>
        )}
      </FormSection>

      <Drawer
        isOpen={isExerciseDrawerOpen}
        onClose={() => {
          setIsExerciseDrawerOpen(false);
          setSelectedExercise(null);
        }}
        title={selectedExercise ? 'Edit Exercise' : 'Add Exercise'}
      >
        <FirearmSpecificExerciseForm
          onSubmit={handleExerciseSubmit}
          onCancel={() => {
            setIsExerciseDrawerOpen(false);
            setSelectedExercise(null);
          }}
          initialValues={selectedExercise?.specific}
          availableExerciseTypes={
            selectedExercise 
              ? exerciseTypes 
              : exerciseTypes.filter(t => !getUsedExerciseTypeIds().has(t.id))
          }
        />
      </Drawer>
    </Form>
  );
};

export default FirearmSpecificForm;
